import dayjs from 'dayjs';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CheckinDetailsCard } from './CheckinDetailsCard';
import { CheckinNoteCard } from './CheckinNoteCard';
import { CheckinState } from '../model/CheckinState';
import { INote } from '../../notes/generic-notes/INote';
import { ICareEvent, ICareEventHours } from '../model/ICheckin';
// IH-6574: Care recipient wellness imports - commented out until feature is released
// import { ICheckinTag, IEmotion, IEnergy, IFeeling, ILifeEvent } from '../model/ICheckin';
// import { Card } from '../../components/Card';
// import { CardHeading } from '../components/CardHeading';
// import { CardSubHeading } from '../components/CardSubHeading';
// import { FeelingSelector } from './FeelingSelector';
// import { EnergySelector } from './EnergySelector';
import { CareMapState } from '../../caremap/CareMapState';
import { CheckinEditorSummary } from './CheckinEditorSummary';
import { Text } from '../../components/Text';
import { Spinner } from '../../components/Spinner';
import { CareTrackerCard } from './CareTrackerCard';
import { CareRecipientSwitcher } from '../../navigation/CareRecipientSwitcher';
import { OrganizationsState } from '../../organizations/OrganizationsState';
import { OrgNameHeading } from '../../organizations/OrgNameHeading';

const useStyles = createUseStyles({
    careMapSwitcher: {
        marginTop: -2,
        marginBottom: -12,
        marginLeft: 6
    }
});

export const CareCheckinEditor: React.FC = () => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const checkinState = useInstance(CheckinState);
    useSubscription(() => checkinState);

    const orgsState = useInstance(OrganizationsState);
    useSubscription(() => orgsState);

    const activeCareMap = careMapState.getActiveCaremap();
    const careCheckin = checkinState.getEditingCheckin('care');

    const checkinDate = careCheckin ? checkinState.getCheckinDate(careCheckin) : undefined;
    const isToday = dayjs(checkinDate).isToday();

    const findEventToAdjust = (careEvents: ICareEvent[], careEventHours: ICareEventHours, direction: -1 | 1, recentlyAdjusted?: ICareEvent[]) => {
        // combine events and hours into array of objects, sorted by hours
        const hoursArray = (careEvents || []).map(careEvent => ({ careEvent, hours: careEventHours[careEvent] || 0 }));
        hoursArray.sort((e1, e2) => direction * ((e1.hours || 0) - (e2.hours || 0)));

        // filter to events that are candidates for adjusting
        const candidates = hoursArray.filter(e => (direction === 1 && e.hours < 24) || (direction === -1 && e.hours > 0)).map(h => h.careEvent);

        // get candidates that have been adjusted, sorted least to most recent
        // (reverse() mutates, so create copy first!)
        recentlyAdjusted = [...recentlyAdjusted || []].reverse();
        recentlyAdjusted = recentlyAdjusted.filter(e => candidates.includes(e));

        // combine arrays to produce list of best to worst, and return best one
        return [...candidates.filter(e => !recentlyAdjusted.includes(e)), ...recentlyAdjusted][0];
    };

    // distribute change in hours across care events
    const distributeHours = (careEventHours: ICareEventHours, careEvents: ICareEvent[], delta: number, recentlyAdjusted?: ICareEvent[]) => {
        const updatedHours = { ...careEventHours };
        const sign = Math.sign(delta) as -1 | 1;
        let change = delta * sign;
        while (change > 0) {
            const event = findEventToAdjust(careEvents, updatedHours, sign, recentlyAdjusted);
            updatedHours[event] = (updatedHours[event] || 0) + sign;
            change--;
        }
        return updatedHours;
    };

    const onAddCareEvent = (careEvent: ICareEvent) => {
        const checkin = checkinState.getEditingCheckin('care')!;
        const careEvents = [ ...(checkin.careEvents || []), careEvent ];
        let careEventHours = checkin.careEventHours || {};
        const totalCareHours = checkin.totalCareHours;

        // if total locked, redistribute total evenly across all events
        if (checkin.totalCareHoursLocked && totalCareHours) {
            careEventHours = distributeHours({}, careEvents, totalCareHours!);
        }

        checkinState.saveCareCheckin({ ...checkin, careEvents, careEventHours });
    };

    const onRemoveCareEvent = (careEvent: ICareEvent) => {
        const checkin = checkinState.getEditingCheckin('care')!;
        const careEvents = (checkin.careEvents || []).filter(e => e !== careEvent);
        let careEventHours = checkin.careEventHours || {};
        let totalCareHours = checkin.totalCareHours;

        // if total locked, redistribute event hours, otherwise reduce total
        const eventHours = careEventHours[careEvent];
        if (eventHours) {
            if (checkin.totalCareHoursLocked) {
                careEventHours = distributeHours(careEventHours, careEvents, eventHours);
            } else if (totalCareHours) {
                totalCareHours -= eventHours;
            }
            delete careEventHours[careEvent];
        }

        // remove this event from adjusted list
        const adjustedCareEvents = (checkin.adjustedCareEvents || []).filter(e => e !== careEvent);

        checkinState.saveCareCheckin({ ...checkin, careEvents, careEventHours, totalCareHours, adjustedCareEvents });
    };

    const onChangeCareEventHours = ({ careEvent, hours }: { careEvent: ICareEvent, hours: number }) => {
        const checkin = checkinState.getEditingCheckin('care')!;
        let totalCareHours = Math.min(checkin.totalCareHours || 0, 24);

        // first handle cases where this event needs to be locked to the total
        if (checkin.totalCareHoursLocked) {
            if (hours > totalCareHours || checkin.careEvents?.length === 1) {
                // if it was already at the total, we need to leave it where it was,
                // so throw an exception to let it know to reset its internal state
                if (checkin.careEventHours?.[careEvent] === totalCareHours) {
                    throw new Error('event hours are locked');
                }
                // otherwise, we can push it back
                hours = totalCareHours;
            }
        }

        // now we can update this event
        let careEventHours = { ...checkin.careEventHours, [careEvent]: hours };

        // adjust other events and total to keep in sync and under daily limit
        const sum = checkin.careEvents!.reduce((s, e) => s += (careEventHours[e] || 0), 0);
        const otherEvents = checkin.careEvents!.filter(e => e !== careEvent);
        if (checkin.totalCareHoursLocked && sum !== totalCareHours) {
            careEventHours = distributeHours(careEventHours, otherEvents, totalCareHours - sum, checkin.adjustedCareEvents);
        } else if (sum > 24) {
            careEventHours = distributeHours(careEventHours, otherEvents, 24 - sum, checkin.adjustedCareEvents);
            totalCareHours = 24;
        } else {
            totalCareHours = sum;
        }

        // move this event to start of adjusted list
        const adjustedCareEvents = [careEvent, ...(checkin.adjustedCareEvents || []).filter(e => e !== careEvent)];

        checkinState.saveCareCheckin({ ...checkin, careEventHours, totalCareHours, adjustedCareEvents });
    };

    const onChangeTotalCareHours = (totalCareHours: number | undefined) => {
        const checkin = checkinState.getEditingCheckin('care')!;
        let careEventHours = checkin.careEventHours;

        // if there are events, increase/reduce their hours to account for this change
        if (checkin.careEvents?.length) {
            const delta = (totalCareHours || 0) - (checkin.totalCareHours || 0);
            if (delta) {
                careEventHours = distributeHours(careEventHours || {}, checkin.careEvents, delta);
            }
        }

        // lock if non-zero value entered, unlock if set to zero/blank
        const totalCareHoursLocked = Boolean(totalCareHours);

        checkinState.saveCareCheckin({ ...checkin, totalCareHours, totalCareHoursLocked, careEventHours });
    };

    const onChangeMilesDriven = (milesDriven: number | undefined) => {
        const checkin = checkinState.getEditingCheckin('care')!;
        checkinState.saveCareCheckin({ ...checkin, milesDriven });
    };

    // IH-6574: Care recipient wellness handlers - commented out until feature is released
    // const onChangeFeeling = async (feeling?: IFeeling) => {
    //     const checkin = checkinState.getEditingCheckin('care')!;
    //     if (feeling !== checkin.feeling) {
    //         checkinState.saveCareCheckin({ ...checkin, feeling });
    //     }
    // };

    // const onChangeEmotions = (tags: ICheckinTag[]) => {
    //     const checkin = checkinState.getEditingCheckin('care')!;
    //     checkinState.saveCareCheckin({ ...checkin, emotions: tags as IEmotion[] });
    // };

    // const onChangeLifeEvents = (tags: ICheckinTag[]) => {
    //     const checkin = checkinState.getEditingCheckin('care')!;
    //     checkinState.saveCareCheckin({ ...checkin, lifeEvents: tags as ILifeEvent[] });
    // };

    // const onChangeEnergy = async (energy?: IEnergy) => {
    //     const checkin = checkinState.getEditingCheckin('care')!;
    //     if (energy !== checkin.energy) {
    //         checkinState.saveCareCheckin({ ...checkin, energy });
    //     }
    // };

    const onChangeNote = (updatedNote: INote) => {
        const checkin = checkinState.getEditingCheckin('care')!;
        checkinState.saveCareCheckin({ ...checkin, note: updatedNote.html, attachments: updatedNote.attachments });
    };

    // IH-6574: commented out until feature is released
    // const patientFirstName = careMapState.getPatientFirstName();

    // in an org, we don't show the switcher, so we include the patient name in the messaging;
    // for non-org we use the general term "your loved one"
    const recipientDescription = (orgsState.isOrgTeamMember() ? <b>{careMapState.getPatientFullName()}'s</b> : <>your loved one's</>);

    return (
        <>
            {orgsState.isOrgTeamMember() &&
                <div style={{marginBottom: -12}}>
                    <OrgNameHeading />
                </div>
            }

            <CheckinEditorSummary>
                Log key observations and care activities to maintain a clear picture of {recipientDescription} needs and progress.
            </CheckinEditorSummary>

            {!orgsState.isOrgTeamMember() &&
                <div className={classes.careMapSwitcher}>
                    <CareRecipientSwitcher
                        triggerId="checkin-editor-switcher"
                        trackingFrom="Journal"
                        createAccountMsg="Please sign in to start journaling!"
                        addRecipientMsg="Please add a care recipient to log care entries."
                    />
                </div>
            }

            {!activeCareMap ? (
                <></>

            ) : (!activeCareMap.links.checkins) ? (
                <Text>You do not have permission to view or edit {careMapState.getPatientFullName()}'s Care Journal.</Text>
            ) : !careCheckin ? (
                <Spinner size="medium" centered />
            ) : (
                <>
                    {/* IH-6574: Care recipient wellness UI - commented out until ready to release
                    <Card padded>
                        <CardHeading>How was {patientFirstName}'s day?</CardHeading>
                        <FeelingSelector value={careCheckin.feeling} onChange={onChangeFeeling} />
                    </Card>

                    <CheckinDetailsCard
                        type="EMOTIONS"
                        title={patientFirstName + "'s Emotions"}
                        tags={careCheckin.emotions || []}
                        onChange={onChangeEmotions}
                    />

                    <CheckinDetailsCard
                        type="LIFE_EVENTS"
                        title={patientFirstName + "'s Life"}
                        tags={careCheckin.lifeEvents || []}
                        onChange={onChangeLifeEvents}
                    />

                    <Card padded>
                        <CardHeading>{patientFirstName}'s Energy</CardHeading>
                        <CardSubHeading>Was {patientFirstName} running on empty or fully charged{isToday ? ' today' : ''}?</CardSubHeading>
                        <EnergySelector value={careCheckin.energy} onChange={onChangeEnergy} />
                    </Card>
                    */}

                    <CheckinDetailsCard
                        type="CARE_EVENTS"
                        tags={careCheckin.careEvents || []}
                        onAdd={t => onAddCareEvent(t as ICareEvent)}
                        onRemove={t => onRemoveCareEvent(t as ICareEvent)}
                    />

                    <CareTrackerCard
                        checkin={careCheckin}
                        patientName={careMapState.getPatientFirstName()}
                        onChangeCareEventHours={onChangeCareEventHours}
                        onChangeTotalCareHours={onChangeTotalCareHours}
                        onChangeMilesDriven={onChangeMilesDriven}
                    />

                    <CheckinNoteCard
                        checkin={careCheckin}
                        heading={isToday ? `Today's Care Note` : 'Care Note'}
                        onChange={onChangeNote}
                    />
                </>
            )}
        </>
    );
};
